import { action, observable, runInAction } from 'mobx';
import type {
  GeneralCustomerResponse,
  GraphData,
  PieChartData,
  ResponseBase,
  TopClients
} from 'tm3-core/api.dtos';
import {
  AlertSearchRequest,
  CustomerRequest,
  DashboardListRequest,
  DashboardResponse
} from 'tm3-core/api.dtos';

import { Colours } from 'bz-core-controls/styles/report-colours';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import popovers from 'bz-stores/lib/diary-popover-store';
import CacheStore from 'bz-utilities/modules/stores/cached-map-store';
import _ from 'lodash';
import moment from 'moment';
import Api from 'tm3-core/api';
import LocationsStore from 'tm3-core/cache/stores/locations-store';
import EConfigKeys from 'tm3-core/enums/config-enum';

const colours = Object.values(Colours);

class DashboardStore {
  client = new JsonServiceClient('/api');
  locations = new LocationsStore();

  constructor() {}

  @observable dnas: GraphData[] = [];
  @observable dnaTotal: object = {};
  @observable cancellations: GraphData[] = [];
  @observable allAppointments: GraphData[] = [];
  @observable appointmentShare: PieChartData[] = [];
  @observable debt: object = {
    aged: []
  };
  @observable newPatientByType = [{}];
  @observable turnover = [{}];
  @observable topClients: TopClients[] = [];
  @observable appointmentStats: object = {
    total: 0,
    cancellations: {},
    dnas: {}
  };

  @observable points: boolean = true;
  @observable dashboard = {};
  @observable appointments: any[] = [];
  @observable selectedAppointment = {};
  @observable doNextOpen = false;
  @observable newPatients = [];
  @observable loadingMoreBottom = false;
  @observable futureDays = 3;
  @observable loadLimiter = 0;
  @observable currentDate = moment(new Date()).startOf('day').toDate();

  @observable showDoNextScreen: boolean;
  @observable user = {};
  @observable appointmentDoNext = {};
  @observable patientDoNext? = {};
  @observable loadingMoreTop: boolean;
  @observable active: boolean;
  @observable statusToggleActive: boolean;

  @action.bound
  async load() {
    this.showDoNextScreen = !!EConfigKeys.DONEXTENABLED;

    const { result: result } = await this.client.get('/dashboard');

    result.dnas[0].colour = 'rgba(255,255,255, 0.3)';
    result.dnas[0].borderColour = 'white';
    result.turnover[0].colour = 'rgba(255,255,255, 0.3)';
    result.turnover[0].borderColour = 'white';
    result.newPatients[0].colour = 'rgba(255,255,255, 0.3)';
    result.newPatients[0].borderColour = 'white';
    result.cancellations[0].colour = 'rgba(255,255,255, 0.3)';
    result.cancellations[0].borderColour = 'white';
    result.allAppointments[0].colour = 'rgba(255,255,255, 0.3)';
    result.allAppointments[0].borderColour = 'white';

    let j = 0;
    let { segments } = result.appointmentShare;
    let withColour = segments.map((s) => {
      s.colour = colours[j];
      j = j < segments.length ? j : 0;
      j++;
      return s;
    });
    let newPatientByType = result.newPatientByType.segments;
    j = 0;
    newPatientByType.map((s) => {
      s.colour = colours[j];
      j = j < newPatientByType.length ? j : 0;
      j++;
      return s;
    });
    runInAction(() => {
      this.newPatientByType = newPatientByType;

      const {
        dnas,
        dnaTotal,
        cancellations,
        allAppointments,
        appointmentShare,
        debt,
        turnover,
        topClients,
        appointmentStats,
        newPatients
      } = result;

      this.dnas = dnas;
      this.dnaTotal = dnaTotal;
      this.cancellations = cancellations;
      this.allAppointments = allAppointments;
      this.appointmentShare = appointmentShare;
      this.debt = debt;
      this.turnover = turnover;
      this.topClients = topClients;
      this.appointmentStats = appointmentStats;

      this.newPatients.replace(newPatients);
    });
  }

  setLocationUserId(location, practitionerId) {
    return (this.user = {
      user: { location, practitionerId }
    });
  }

  @action.bound
  doNextPrompt(apptId) {
    if (this.showDoNextScreen) {
      const appointment = (this.appointments || []).filter((x) => x.id === parseInt(apptId));
      if (appointment[0] != null) {
        this.appointmentDoNext = appointment[0];
        return this.setPatientForDoNext(appointment[0].customerId).then(() => {
          return (this.doNextOpen = true);
        });
      }
    }
  }

  setPatientForDoNext(patientId) {
    return this.client
      .get(new CustomerRequest(), patientId)
      .then((res: ResponseBase<GeneralCustomerResponse>) => {
        const patient = res.result != null ? res.result : undefined;
        return (this.patientDoNext = patient);
      });
  }

  closePopover() {
    return (this.doNextOpen = false);
  }

  @action.bound
  async loadAppointments(initialLoad = false) {
    if (
      this.loadingMoreBottom ||
      !this.user ||
      ((initialLoad === true && this.appointments) ?? [].length === 0) ||
      initialLoad === false
    ) {
      const currentDate = moment(this.currentDate);
      const startDate = currentDate.toDate();
      const endDate = currentDate.clone().add(this.futureDays, 'days').toDate();

      this.loadingMoreBottom = true;
      const req = new DashboardListRequest({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      const res: any[] = await this.client.post(req);

      runInAction(() => {
        let appointments: any[] = this.appointments || [];

        const idsNotToInclude = res.map((y) => y.id);
        appointments = appointments.filter((x) => !idsNotToInclude.includes(x.id));
        appointments = _.union(appointments, res);
        this.currentDate =
          res.length > 0
            ? moment(res[res.length - 1].endDateTime)
                .add(1, 's')
                .toDate()
            : endDate;
        this.appointments = appointments;
        this.loadingMoreBottom = false;
        this.loadingMoreTop = false;
      });
    }
  }

  selectAppointment(appt) {
    appt.location = this.locations.All.filter({ id: appt.locationId }).value()[0];
    return new Promise((resolve, reject) => {
      if (appt.customerId > 0 && !appt.patient) {
        this.client.get('/customers', appt.customerId).then((res) => {
          appt.patient = res;
          this.selectedAppointment = appt;
          popovers.selected = appt;
          resolve(appt);
        });
      } else {
        this.selectedAppointment = appt;
        popovers.selected = appt;
        resolve(appt);
      }
    });
  }

  @action.bound
  async refreshAlerts(id) {
    if (!id) {
      return;
    }

    const appointments = this.appointments;
    const discriminatorType = 'A';
    let existing = _.find(appointments, { id: parseInt(id) });
    if (!existing) {
      existing = _.find(appointments, { customerId: parseInt(id) });
    }
    if (!existing) {
      return;
    }

    const req = new AlertSearchRequest({
      objectId: existing.id,
      discriminatorType: 'A'
    });

    let res = await this.client.post(req);
    existing.alerts = (res != null ? res : undefined) || [];
    let idx = _.findIndex(appointments, (x) => x.id === existing.id);
    appointments.splice(idx, 1, existing);
    runInAction(() => {
      this.appointments = appointments;
    });
  }

  refreshAppointmentAuth(id, authLeft) {
    let appointments = this.appointments;
    const existing = _.find(appointments, { id });
    if (existing) {
      existing.authLeft = authLeft;
      this.refreshAppointment(id, existing);
    }
  }

  @action.bound
  async refreshAppointment(id, appt = null) {
    id = parseInt(id);
    if (!!appt) {
      id = appt.id;
    }
    let appointments = this.appointments;
    const existing = _.find(appointments, { id });

    if (appt) {
      if (existing) {
        appt.startDateTime = moment(appt.startDateTime).format('YYYY-MM-DDTHH:mm');
        appointments = (this.appointments || []).filter((x) => x.id !== appt.id);
        appointments = appointments.concat(appt);
        this.appointments = _.sortBy(appointments, 'startDateTime');
        return;
      } else {
        appointments.push(appt);
        this.appointments = _.sortBy(appointments, 'startDateTime');
        return;
      }
    }

    if (!existing) {
      return;
    }

    const req = new DashboardListRequest({
      startDate: moment(existing.startDateTime).startOf('day').toDate().toISOString(),
      endDate: moment(existing.endDateTime).startOf('day').toDate().toISOString()
    });
    let res = await this.client.post(req);
    appointments = _.reject(this.appointments || [], (x) => _.includes(_.map(res, 'id'), x.id));
    appointments = _.union(appointments, res);
    runInAction(() => {
      this.appointments = _.sortBy(appointments, 'startDateTime');
    });
  }

  @action.bound
  deleteAppointmentOrEvent(apptId): void {
    let id = parseInt(apptId);

    const existing = _.find(this.appointments, { id });

    if (existing) {
      const latestAppointments = (this.appointments || []).filter((x) => x.id !== id);
      this.appointments = _.sortBy(latestAppointments, 'startDateTime');
    }
  }

  @action.bound
  deleteRecurringAppointmentsOrEvents(apptDeleteResp: any): void {
    if (!apptDeleteResp) {
      return;
    }
    let apptIds: number[] = [];

    for (const key in apptDeleteResp) {
      var sessions = apptDeleteResp[key];
      apptIds.push(...sessions.map((appt) => appt.id));
    }

    const latestAppointments = (this.appointments || []).filter((x) => !apptIds.includes(x.id));
    this.appointments = _.sortBy(latestAppointments, 'startDateTime');
  }

  @action.bound
  storeCurrentTab(currentTab, statusToggleActive) {
    if (currentTab != null) {
      this.active = currentTab;
    }
    if (statusToggleActive != null) {
      return this.statusToggleActive;
    }
  }
}

export default new DashboardStore();
